import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Search, AlertTriangle, Pill } from 'lucide-react';
import { fetchWithAuth } from '../utils/api';
import { useLanguage } from './LanguageContext';
import { calculateLine, resolveVatRate } from '../utils/vat';
import { branchVatRate as branchRate } from '../utils/vatConfig';
import Pagination from './Pagination';

const DOSAGE_FORMS = ['tablet', 'capsule', 'liquid', 'injection', 'topical',
                      'gel', 'rinse', 'powder', 'spray', 'other'];

const BLANK = {
  name: '', generic_name: '', category_id: '', dosage_form: 'tablet', strength: '',
  unit: '', price_per_unit: '', vat_mode: 'none', vat_rate: '', manufacturer: '',
  description: '', prescription_required: true, stock_quantity: '0',
  min_stock_level: '10', expiry_date: '', batch_number: '', status: 'active',
};

const Medications = ({ currentBranch }) => {
  const { print_lang, currentLanguage } = useLanguage();

  const [medications, setMedications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(BLANK);
  const [formError, setFormError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const branchId = currentBranch?.branch_id;
  const branchVatRate = branchRate(currentBranch);

  const catName = (row) =>
    currentLanguage === 'th'
      ? (row.category_name_th || row.category_name_en || '-')
      : (row.category_name_en || '-');

  const fetchMedications = useCallback(async () => {
    if (!branchId) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        branch_id: branchId, page: currentPage, limit: pageSize, status: 'all',
      });
      if (searchTerm) params.set('search', searchTerm);
      if (categoryFilter) params.set('category_id', categoryFilter);
      const res = await fetchWithAuth(`/api/medications?${params}`);
      setMedications(res?.data || []);
      setTotalPages(res?.pagination?.totalPages || 0);
      setTotalCount(res?.pagination?.totalCount || 0);
    } catch (err) {
      console.error('Failed to load medications:', err);
      setMedications([]);
    } finally {
      setIsLoading(false);
    }
  }, [branchId, currentPage, pageSize, searchTerm, categoryFilter]);

  useEffect(() => { fetchMedications(); }, [fetchMedications]);

  useEffect(() => {
    if (!branchId) return;
    fetchWithAuth('/api/medications/categories')
      .then((res) => setCategories(res?.data || []))
      .catch(() => setCategories([]));
    fetchWithAuth(`/api/medications/low-stock?branch_id=${branchId}`)
      .then((res) => setLowStockCount((res?.data || []).length))
      .catch(() => setLowStockCount(0));
  }, [branchId, medications.length]);

  const openAdd = () => {
    setModalMode('add'); setSelected(null); setFormData(BLANK); setFormError('');
    setShowModal(true);
  };

  const openEdit = (m) => {
    setModalMode('edit'); setSelected(m); setFormError('');
    setFormData({
      name: m.name || '', generic_name: m.generic_name || '',
      category_id: m.category_id ?? '', dosage_form: m.dosage_form || 'tablet',
      strength: m.strength || '', unit: m.unit || '',
      price_per_unit: m.price_per_unit ?? '',
      vat_mode: m.vat_mode || 'none',
      vat_rate: m.vat_rate ?? '',
      manufacturer: m.manufacturer || '', description: m.description || '',
      prescription_required: Number(m.prescription_required) === 1,
      stock_quantity: m.stock_quantity ?? '0', min_stock_level: m.min_stock_level ?? '10',
      // A DATE comes back as an ISO timestamp; the date input needs yyyy-mm-dd.
      expiry_date: m.expiry_date ? String(m.expiry_date).slice(0, 10) : '',
      batch_number: m.batch_number || '', status: m.status || 'active',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.name.trim()) {
      setFormError(print_lang('medication_name_required', 'Medication name is required'));
      return;
    }
    if (!formData.unit.trim()) {
      setFormError(print_lang('unit_required', 'Unit is required'));
      return;
    }
    try {
      const body = { ...formData, branch_id: branchId };
      if (modalMode === 'edit') {
        await fetchWithAuth(`/api/medications/${selected.medication_id}`, {
          method: 'PUT', body: JSON.stringify(body),
        });
      } else {
        await fetchWithAuth('/api/medications', {
          method: 'POST', body: JSON.stringify(body),
        });
      }
      setShowModal(false);
      fetchMedications();
    } catch (err) {
      setFormError(err.message || print_lang('save_failed', 'Save failed'));
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(print_lang('confirm_delete', 'Delete this item?'))) return;
    try {
      await fetchWithAuth(`/api/medications/${m.medication_id}`, { method: 'DELETE' });
      fetchMedications();
    } catch (err) {
      window.alert(
        err.code === 'MEDICATION_IN_USE'
          ? print_lang('medication_in_use',
              'This medication has been prescribed and cannot be deleted')
          : (err.message || print_lang('delete_failed', 'Delete failed'))
      );
    }
  };

  const priceInclVat = (row) => {
    const rate = resolveVatRate({
      vatMode: row.vat_mode, itemRate: row.vat_rate, branchRate: branchVatRate,
    });
    return calculateLine({
      quantity: 1, unit_price: row.price_per_unit, vat_mode: row.vat_mode, vat_rate: rate,
    }).total;
  };

  // Within 90 days, or already past.
  const expiringSoon = (d) => {
    if (!d) return false;
    const limit = new Date();
    limit.setDate(limit.getDate() + 90);
    return new Date(d) <= limit;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{print_lang('medications', 'Medications')}</h1>
          {lowStockCount > 0 && (
            <span className="badge badge-warning flex items-center gap-1">
              <AlertTriangle size={14} />
              {lowStockCount} {print_lang('low_stock', 'Low Stock')}
            </span>
          )}
        </div>
        <button className="btn btn-primary flex items-center gap-2" onClick={openAdd}>
          <Plus size={18} /> {print_lang('add_medication', 'Add Medication')}
        </button>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            className="w-full border rounded pl-10 pr-3 py-2"
            placeholder={print_lang('search_medications', 'Search medications...')}
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
        </div>
        <select className="border rounded px-3 py-2" value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}>
          <option value="">{print_lang('all_categories', 'All Categories')}</option>
          {categories.map((c) => (
            <option key={c.category_id} value={c.category_id}>
              {currentLanguage === 'th' ? (c.name_th || c.name_en) : c.name_en}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">{print_lang('loading', 'Loading...')}</div>
      ) : medications.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Pill size={40} className="mx-auto mb-2 text-gray-300" />
          {print_lang('no_medications', 'No medications found')}
        </div>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>{print_lang('medication', 'Medication')}</th>
                <th>{print_lang('category', 'Category')}</th>
                <th>{print_lang('dosage_form', 'Dosage Form')}</th>
                <th>{print_lang('unit', 'Unit')}</th>
                <th className="text-right">{print_lang('price_per_unit', 'Price per Unit')}</th>
                <th className="text-right">{print_lang('price_incl_vat', 'Price incl. VAT')}</th>
                <th className="text-right">{print_lang('stock_quantity', 'Stock')}</th>
                <th>{print_lang('expiry_date', 'Expiry Date')}</th>
                <th>{print_lang('status', 'Status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {medications.map((m) => {
                const low = Number(m.stock_quantity) <= Number(m.min_stock_level);
                const exp = expiringSoon(m.expiry_date);
                return (
                  <tr key={m.medication_id}>
                    <td>
                      <div className="font-medium">
                        {m.name}
                        {m.strength ? ` ${m.strength}` : ''}
                      </div>
                      {m.generic_name && (
                        <div className="text-sm text-gray-500">{m.generic_name}</div>
                      )}
                    </td>
                    <td>{catName(m)}</td>
                    <td>{print_lang(m.dosage_form, m.dosage_form)}</td>
                    <td>{m.unit}</td>
                    <td className="text-right">{Number(m.price_per_unit).toFixed(2)}</td>
                    <td className="text-right font-medium">{priceInclVat(m).toFixed(2)}</td>
                    <td className={`text-right ${low ? 'text-red-600 font-semibold' : ''}`}>
                      {Number(m.stock_quantity).toFixed(0)}
                    </td>
                    <td className={exp ? 'text-orange-600 font-medium' : ''}>
                      {m.expiry_date ? String(m.expiry_date).slice(0, 10) : '-'}
                    </td>
                    <td>
                      <span className={`badge ${m.status === 'active' ? 'badge-success' : 'badge-error'}`}>
                        {print_lang(m.status, m.status)}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button className="p-2 hover:bg-gray-100 rounded" onClick={() => openEdit(m)}
                                title={print_lang('edit', 'Edit')}>
                          <Edit2 size={16} className="text-blue-600" />
                        </button>
                        <button className="p-2 hover:bg-red-50 rounded" onClick={() => handleDelete(m)}
                                title={print_lang('delete', 'Delete')}>
                          <Trash2 size={16} className="text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
            print_lang={print_lang}
          />
        </>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowModal(false);
        }}>
          <div className="modal-content w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {modalMode === 'edit'
                ? print_lang('edit_medication', 'Edit Medication')
                : print_lang('add_medication', 'Add Medication')}
            </h2>

            {formError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4">
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('medication', 'Medication')} *
                  </label>
                  <input type="text" className="w-full border rounded px-3 py-2" required
                         value={formData.name}
                         onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('generic_name', 'Generic Name')}
                  </label>
                  <input type="text" className="w-full border rounded px-3 py-2"
                         value={formData.generic_name}
                         onChange={(e) => setFormData({ ...formData, generic_name: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('category', 'Category')}</label>
                  <select className="w-full border rounded px-3 py-2" value={formData.category_id}
                          onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}>
                    <option value="">-</option>
                    {categories.map((c) => (
                      <option key={c.category_id} value={c.category_id}>
                        {currentLanguage === 'th' ? (c.name_th || c.name_en) : c.name_en}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('dosage_form', 'Dosage Form')}
                  </label>
                  <select className="w-full border rounded px-3 py-2" value={formData.dosage_form}
                          onChange={(e) => setFormData({ ...formData, dosage_form: e.target.value })}>
                    {DOSAGE_FORMS.map((f) => (
                      <option key={f} value={f}>{print_lang(f, f)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('strength', 'Strength')}</label>
                  <input type="text" className="w-full border rounded px-3 py-2" placeholder="500 mg"
                         value={formData.strength}
                         onChange={(e) => setFormData({ ...formData, strength: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('unit', 'Unit')} *</label>
                  <input type="text" className="w-full border rounded px-3 py-2" required
                         value={formData.unit}
                         onChange={(e) => setFormData({ ...formData, unit: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('price_per_unit', 'Price per Unit')}
                  </label>
                  <input type="number" step="0.01" min="0" className="w-full border rounded px-3 py-2"
                         value={formData.price_per_unit}
                         onChange={(e) => setFormData({ ...formData, price_per_unit: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('stock_quantity', 'Stock')}</label>
                  <input type="number" step="1" min="0" className="w-full border rounded px-3 py-2"
                         value={formData.stock_quantity}
                         onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('min_stock_level', 'Minimum Stock')}
                  </label>
                  <input type="number" step="1" min="0" className="w-full border rounded px-3 py-2"
                         value={formData.min_stock_level}
                         onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('expiry_date', 'Expiry Date')}
                  </label>
                  <input type="date" className="w-full border rounded px-3 py-2"
                         value={formData.expiry_date}
                         onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded">
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('vat_mode', 'VAT')}</label>
                  <select className="w-full border rounded px-3 py-2" value={formData.vat_mode}
                          onChange={(e) => setFormData({ ...formData, vat_mode: e.target.value })}>
                    <option value="none">{print_lang('vat_none', 'No VAT')}</option>
                    <option value="include">{print_lang('vat_include', 'Price includes VAT')}</option>
                    <option value="exclude">{print_lang('vat_exclude', 'Price excludes VAT')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('vat_rate_override', 'VAT Rate Override')}
                  </label>
                  <input type="number" step="0.01" min="0" max="100"
                         className="w-full border rounded px-3 py-2"
                         placeholder={`${print_lang('use_branch_rate', 'Use branch rate')} (${branchVatRate}%)`}
                         disabled={formData.vat_mode === 'none'}
                         value={formData.vat_rate}
                         onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('price_incl_vat', 'Price incl. VAT')}
                  </label>
                  <div className="px-3 py-2 font-medium">
                    {calculateLine({
                      quantity: 1,
                      unit_price: formData.price_per_unit === '' ? 0 : formData.price_per_unit,
                      vat_mode: formData.vat_mode,
                      vat_rate: resolveVatRate({
                        vatMode: formData.vat_mode,
                        itemRate: formData.vat_rate,
                        branchRate: branchVatRate,
                      }),
                    }).total.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('manufacturer', 'Manufacturer')}
                  </label>
                  <input type="text" className="w-full border rounded px-3 py-2"
                         value={formData.manufacturer}
                         onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {print_lang('batch_number', 'Batch Number')}
                  </label>
                  <input type="text" className="w-full border rounded px-3 py-2"
                         value={formData.batch_number}
                         onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{print_lang('status', 'Status')}</label>
                  <select className="w-full border rounded px-3 py-2" value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                    <option value="active">{print_lang('active', 'Active')}</option>
                    <option value="inactive">{print_lang('inactive', 'Inactive')}</option>
                    <option value="discontinued">{print_lang('discontinued', 'Discontinued')}</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.prescription_required}
                         onChange={(e) => setFormData({
                           ...formData, prescription_required: e.target.checked,
                         })} />
                  <span className="text-sm font-medium">
                    {print_lang('prescription_required', 'Prescription Required')}
                  </span>
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" className="btn" onClick={() => setShowModal(false)}>
                  {print_lang('cancel', 'Cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {print_lang('save', 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Medications;
